// Shared environment materials: weathered planks, fine timber, painted wood, fabric and metal.
// Wood uses the CC0 Poly Haven textures built by `npm run assets`; if they are missing the
// materials fall back to plain colours so the world still renders.
import { Color, MeshStandardMaterial, Vector2, type Texture } from 'three';
import type { AssetEntry, AssetStore } from '../assets';
import { tiled } from './util';

const WOODS = ['weathered_brown_planks', 'fine_grained_wood'] as const;

/** The environment's asset entries (title-backdrop stage, about 1 MB). */
export function environmentAssetEntries(): AssetEntry[] {
  return WOODS.flatMap((w) => [
    { key: `env/${w}/diff`, kind: 'colorTexture' as const, url: `assets/env/${w}_diff.webp` },
    { key: `env/${w}/nor`, kind: 'texture' as const, url: `assets/env/${w}_nor.webp` },
    { key: `env/${w}/arm`, kind: 'texture' as const, url: `assets/env/${w}_arm.webp` },
  ]);
}

export interface EnvMaterials {
  /** Grandstand boards, edging, stall partitions. Weathered, warm brown. */
  planks: MeshStandardMaterial;
  /** Posts, beams, gantry towers: fine-grained timber. */
  timber: MeshStandardMaterial;
  /** Roof boards: dark stained planks. */
  roof: MeshStandardMaterial;
  /** Maroon painted wood (stalls, podium skirts). */
  paintedMaroon: MeshStandardMaterial;
  /** Cream painted wood (rails, trims). */
  paintedCream: MeshStandardMaterial;
  /** Gold paint (trims, numbers). */
  gold: MeshStandardMaterial;
  /** Dark iron (fittings, bulb wires, poles). */
  iron: MeshStandardMaterial;
}

function woodMaterial(store: AssetStore, name: (typeof WOODS)[number], tint: string, fallback: string, roughness = 1): MeshStandardMaterial {
  const key = `env/${name}`;
  const has = store.has(`${key}/diff`);
  const m = new MeshStandardMaterial({ color: has ? tint : fallback, roughness, metalness: 0, vertexColors: true });
  if (has) {
    m.map = tiled(store.texture(`${key}/diff`));
    m.normalMap = tiled(store.texture(`${key}/nor`));
    m.normalScale = new Vector2(1, 1);
    const arm: Texture = tiled(store.texture(`${key}/arm`));
    m.roughnessMap = arm;
    m.aoMap = null; // AO comes from N8AO and the baked vertex colours
  }
  m.name = name;
  return m;
}

export function createMaterials(store: AssetStore): EnvMaterials {
  const planks = woodMaterial(store, 'weathered_brown_planks', '#d8c0a4', '#7a5a40');
  const timber = woodMaterial(store, 'fine_grained_wood', '#c7a17c', '#6a4630', 0.9);
  const roof = woodMaterial(store, 'weathered_brown_planks', '#7c4a3a', '#4a2a22');
  const paintedMaroon = woodMaterial(store, 'weathered_brown_planks', '#a83a52', '#6e1f33', 0.75);
  paintedMaroon.color = new Color('#b0405a');
  const paintedCream = woodMaterial(store, 'fine_grained_wood', '#fff1dc', '#e9dcc4', 0.7);
  const gold = new MeshStandardMaterial({ color: '#e0a83c', roughness: 0.38, metalness: 0.85, vertexColors: true, name: 'gold' });
  const iron = new MeshStandardMaterial({ color: '#2a2622', roughness: 0.55, metalness: 0.6, vertexColors: true, name: 'iron' });
  return { planks, timber, roof, paintedMaroon, paintedCream, gold, iron };
}
